/**
 * Barcodes and QR codes (`code` canvas element and `barcode` page block). doc-render draws them as
 * SVG with bwip-js; this module owns the typed props and the value rules per symbology.
 * Owner: doc-engine (wave 1), doc-templates (wave 2).
 */
import { z } from 'zod';
import { ColorRef } from './color';
import { ExprSource, type ExprSite } from './expr-site';

export const SYMBOLOGIES = ['qr', 'code128', 'ean13', 'datamatrix', 'pdf417'] as const;
export const Symbology = z.enum(SYMBOLOGIES, { message: 'Unknown symbology' });
export type Symbology = z.infer<typeof Symbology>;

/** EAN-13 check digit of the first twelve digits. */
export function ean13CheckDigit(first12: string): number {
  const sum = [...first12].reduce((acc, d, i) => acc + Number(d) * (i % 2 === 0 ? 1 : 3), 0);
  return (10 - (sum % 10)) % 10;
}

/** Why a static value cannot be encoded in a symbology, or null when it can. */
export function codeValueProblem(symbology: Symbology, value: string): string | null {
  if (!value) return 'Enter a value to encode';
  switch (symbology) {
    case 'ean13':
      if (!/^\d{12,13}$/.test(value)) return 'EAN-13 needs 12 or 13 digits';
      if (value.length === 13 && Number(value[12]) !== ean13CheckDigit(value.slice(0, 12)))
        return 'The EAN-13 check digit is wrong';
      return null;
    case 'code128':
      if (value.length > 80) return 'Code 128 holds at most 80 characters';
      if (!/^[\x20-\x7E]+$/.test(value)) return 'Code 128 takes printable ASCII characters only';
      return null;
    case 'qr':
      return new TextEncoder().encode(value).length > 2953 ? 'A QR code holds at most 2,953 bytes' : null;
    case 'datamatrix':
      return new TextEncoder().encode(value).length > 1556
        ? 'A Data Matrix code holds at most 1,556 bytes'
        : null;
    case 'pdf417':
      return new TextEncoder().encode(value).length > 1850 ? 'A PDF417 code holds at most 1,850 bytes' : null;
  }
}

export const CodeValue = z.union([z.string().max(4000), z.object({ expr: ExprSource }).strict()]);
export type CodeValue = z.infer<typeof CodeValue>;

/** Props shared by the canvas `code` element and the `barcode` block. */
export const codeProps = {
  symbology: Symbology,
  /** A static value, or an expression evaluated against the bound record at render time. */
  value: CodeValue,
  /** Quiet zone in modules (bars) around the code. */
  quietZone: z.number().int().min(0).max(20).default(4),
  colors: z
    .object({ foreground: ColorRef, background: ColorRef.nullable() })
    .strict()
    .default({ foreground: { token: 'ink' }, background: null }),
  /** Human-readable text under linear codes. */
  showText: z.boolean().default(true),
  /** QR error correction level. */
  errorCorrection: z.enum(['L', 'M', 'Q', 'H']).default('M'),
};

/** The properties of a code as a renderer reads them from a node's attributes (extra attributes are ignored). */
export const CodeSpec = z.object(codeProps);
export type CodeSpec = z.infer<typeof CodeSpec>;

export function refineCode(value: { symbology: Symbology; value: CodeValue }, ctx: z.RefinementCtx): void {
  if (typeof value.value !== 'string') return;
  const problem = codeValueProblem(value.symbology, value.value);
  if (problem) ctx.addIssue({ code: 'custom', path: ['value'], message: problem });
}

export function codeExprSites(value: CodeValue, prefix: (string | number)[] = []): ExprSite[] {
  return typeof value === 'string'
    ? []
    : [
        {
          path: [...prefix, 'value', 'expr'],
          source: value.expr,
          role: 'expression',
          context: 'document',
          expected: { kind: 'text' },
        },
      ];
}
